// Cash-incentives tracker.
// Records the daily cash / EFT payouts each outreach team (Team 1 =
// George, Team 2 = Racquel) hands to patients for enrolments,
// referrals, health-card recovery, etc. Backed by
// /api/consultations/incentives/*.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import { Plus, ClipboardPaste, Pencil, Trash2, X as XIcon, Check, Copy, Lock } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { api } from "@/lib/consult/api";
import AdherencePanel from "./AdherencePanel";
import TableColumnHeader from "./TableColumnHeader";
import { useTableView, applyTableView, uniqueValues, uniqueValuePairs, hasActiveFilters } from "./tableView";

// Palette (matches OutreachPanel / Scheduling dashboard)
const DARK_GREEN = "#0F3B2E";
const LABEL_GREEN = "#2D5A4C";
const TEXT = "#1C2B26";
const MUTED = "#6B7A73";
const BORDER = "#E5E2DC";
const NAVY = "#0F3B2E";
const TAG_CASH = "#2E7D5B";
const TAG_EFT = "#4A6FB0";

// Fallback until the shared staff/team registry loads — new staff added in
// Users admin appear as their own Team N (XX) buttons automatically.
const DEFAULT_TEAMS = [
  { key: "team_1", label: "Team 1 (G)" },
  { key: "team_2", label: "Team 2 (R)" },
  { key: "team_3", label: "Team 3 (M)" },
  { key: "adherence", label: "HQ" },
];

// Module-level caches — this panel unmounts whenever its tab is hidden, and
// without them every flip back flashes "Loading…" and pops the locked team
// segment into the strip a beat late.
const TEAMS_CACHE = { teams: null };
const DATA_CACHE = new Map(); // `${team}|${month}|${day}` -> { records, summary, reasons, topups, cashVerifs }

const labelStyle = {
  fontSize: 11, fontWeight: 700, letterSpacing: "0.16em",
  textTransform: "uppercase", color: LABEL_GREEN, margin: 0, marginBottom: 4,
};

const inputStyle = {
  width: "100%", maxWidth: "100%", minWidth: 0, height: 38,
  padding: "0 12px", fontSize: 14, fontFamily: "inherit",
  borderRadius: 8, border: `1px solid ${BORDER}`, background: "#fff",
  color: TEXT, boxSizing: "border-box",
};

// Available months: last 6 → current → next 3 (matches inventory picker).
const buildMonths = () => {
  const list = [];
  const now = new Date();
  for (let i = -6; i <= 3; i += 1) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = d.toLocaleString(undefined, { month: "short", year: "numeric" });
    list.push({ key, label });
  }
  return list;
};

const currentMonthKey = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const fromYMD = (ymd) => {
  const [y, m, d] = (ymd || "").split("-").map(Number);
  if (!y) return null;
  return new Date(y, m - 1, d);
};

const toYMD = (d) => {
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const money = (n) => {
  const v = Number(n) || 0;
  // Drop trailing .00 for whole dollars (keeps things compact on mobile).
  const sign = v < 0 ? "-" : "";
  const a = Math.abs(v);
  return Number.isInteger(a) ? `${sign}$${a}` : `${sign}$${a.toFixed(2)}`;
};

// Compact date picker that avoids the iOS native calendar overlay.
// Displays "MMM d, yyyy" on a button; opens a small shadcn calendar
// popover on tap. Matches the pattern used in Dashboard.jsx's
// reschedule flow.
function DatePickerButton({ value, onChange, testId }) {
  const [open, setOpen] = useState(false);
  const selected = value ? fromYMD(value) : null;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          style={{
            ...inputStyle,
            textAlign: "left",
            display: "flex", alignItems: "center",
            cursor: "pointer",
          }}
        >
          {selected ? format(selected, "MMM d, yyyy") : "Pick a date"}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-2" data-testid={`${testId}-popover`}>
        <Calendar
          mode="single"
          selected={selected || undefined}
          onSelect={(d) => {
            if (d) {
              onChange(toYMD(d));
              setOpen(false);
            }
          }}
          className="p-0"
        />
      </PopoverContent>
    </Popover>
  );
}

// Parse a patient CRM label into { first_name, last_name }. Handles the
// same shapes as the scheduling "Paste patient" flow.
const parsePatientLabel = (raw) => {
  const out = { first_name: "", last_name: "" };
  if (!raw || typeof raw !== "string") return out;
  const cleaned = raw
    .replace(/\r/g, "")
    .replace(/[\u200B-\u200D\uFEFF]/g, "");
  const lines = cleaned.split("\n").map((l) => l.trim()).filter(Boolean);
  let full = "";
  const nameLabel = cleaned.match(/\b(?:RE|Name|Patient(?:\s*name)?)\s*:\s*([^\n]+)/i);
  if (nameLabel) {
    full = nameLabel[1].trim();
  } else {
    for (const line of lines) {
      if (/\d/.test(line)) continue;
      if (/\b(street|st\.?|ave\.?|blvd\.?|road|rd\.?|drive|dr\.?|apt|unit|ontario|quebec|ON|QC)\b/i.test(line)) continue;
      const m = line.match(/^([A-Za-zÀ-ÿ'\- ]+),\s*([A-Za-zÀ-ÿ'\- ]+)$/);
      if (m) { full = `${m[2].trim()} ${m[1].trim()}`; break; }
    }
  }
  if (!full) return out;
  // Split "First [Middle] Last" — first token is first name, rest is last.
  const parts = full.split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    out.first_name = parts[0];
  } else {
    out.first_name = parts[0];
    out.last_name = parts.slice(1).join(" ");
  }
  return out;
};

// ============================================================================
export default function IncentivesPanel({ refreshKey = 0, openRequest = null, isAdmin = false, lockTeam = null, lockTeamLabel = null }) {
  const months = useMemo(() => buildMonths(), []);
  const [team, setTeam] = useState(lockTeam || DEFAULT_TEAMS[0].key);
  // Staff-scoped logins are locked to their own team (same rule as Expenses).
  useEffect(() => { if (lockTeam && team !== lockTeam) setTeam(lockTeam); }, [lockTeam, team]);
  // Live team list from the shared registry (Team 4 (XX), Team 5 (XX), …
  // appear here as soon as new staff are added in Users admin).
  const [teams, setTeams] = useState(TEAMS_CACHE.teams || DEFAULT_TEAMS);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/staff-teams");
        if (Array.isArray(data?.teams) && data.teams.length) {
          const merged = [...data.teams.map((t) => ({ key: t.key, label: t.label })), { key: "adherence", label: "HQ" }];
          TEAMS_CACHE.teams = merged;
          setTeams(merged);
        }
      } catch { /* keep defaults */ }
    })();
  }, []);
  const [month, setMonth] = useState(currentMonthKey());
  const [day, setDay] = useState("all"); // "all" | YYYY-MM-DD

  // Hydrate from the module cache on remount so the panel renders instantly
  // (no "Loading…" flash) while the fresh fetch runs in the background.
  const cached0 = DATA_CACHE.get(`${lockTeam || DEFAULT_TEAMS[0].key}|${currentMonthKey()}|all`) || null;
  const [records, setRecords] = useState(cached0?.records || []);
  const [summary, setSummary] = useState(cached0?.summary || {
    records: 0, cash_total: 0, cash_count: 0,
    eft_total: 0, eft_count: 0, total: 0,
    days_with_records: [],
    topups_total: 0, topups_count: 0,
    month_paid_total: 0, month_paid_records: 0,
    remaining: 0,
  });
  const [reasons, setReasons] = useState(cached0?.reasons || []);
  const [topups, setTopups] = useState(cached0?.topups || []);
  // Daily cash-float verifications: a record can only be saved on a date that
  // has a verification entry for the selected team (any date — today, a
  // future date, or a backfilled one). pendingCashDate = date that must be
  // verified before the pending Add-record can save.
  const [cashVerifs, setCashVerifs] = useState(cached0?.cashVerifs || []);
  const [pendingCashDate, setPendingCashDate] = useState(null);
  const [addAfterVerify, setAddAfterVerify] = useState(false); // open the Add form right after verifying
  const verifiedCashDates = useMemo(
    () => new Set(cashVerifs.map((v) => v.date)),
    [cashVerifs]
  );
  const todayStr = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  })();
  const [loading, setLoading] = useState(false);

  const [editing, setEditing] = useState(null); // record obj or "new"
  const [manageOpen, setManageOpen] = useState(false);
  const [balanceOpen, setBalanceOpen] = useState(false);

  // Sortable / filterable / searchable view (persisted per session).
  const [view, updateView] = useTableView("consult.incentives.view.v1");
  const fmtDate = useCallback((ymd) => {
    const d = fromYMD(ymd);
    return d ? format(d, "MMM d, yyyy") : (ymd || "");
  }, []);
  const tableColumns = useMemo(() => ({
    date:       { get: (r) => r.date, displayGet: (r) => fmtDate(r.date), type: "date" },
    first_name: { get: (r) => r.first_name || "" },
    last_name:  { get: (r) => r.last_name || "" },
    reason:     { get: (r) => r.reason || "" },
    type:       { get: (r) => ((r.type || "cash").toLowerCase() === "eft" ? "EFT" : "Cash") },
    amount:     { get: (r) => Number(r.amount) || 0, type: "number" },
    verified:   { get: (r) => (r.verified ? "Verified" : "Not verified") },
  }), [fmtDate]);

  const initials = (name) => {
    const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return "";
    if (parts.length === 1) return parts[0];
    return parts.map((p) => p[0] || "").join("").toUpperCase().slice(0, 3);
  };

  // Audit toggle: confirm a record matches the system (reconciliation).
  // Admin-only — the server enforces it too.
  const toggleVerify = async (r) => {
    try {
      await api.patch(`/incentives/records/${r.id}`, { verified: !r.verified });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update the record");
    }
  };
  const displayRecords = useMemo(
    () => applyTableView(records, view, tableColumns, ["date", "first_name", "last_name", "reason"]),
    [records, view, tableColumns]
  );
  const uniqDates      = useMemo(() => uniqueValuePairs(records, (r) => r.date, (r) => fmtDate(r.date)), [records, fmtDate]);
  const uniqFirstNames = useMemo(() => uniqueValues(records, (r) => r.first_name), [records]);
  const uniqLastNames  = useMemo(() => uniqueValues(records, (r) => r.last_name), [records]);
  const uniqReasons    = useMemo(() => uniqueValues(records, (r) => r.reason), [records]);
  const uniqTypes      = useMemo(() => uniqueValues(records, (r) => ((r.type || "cash").toLowerCase() === "eft" ? "EFT" : "Cash")), [records]);
  const uniqAmounts    = useMemo(() => {
    const map = new Map();
    records.forEach((r) => {
      const n = Number(r.amount) || 0;
      const key = String(n);
      if (!map.has(key)) map.set(key, { value: key, label: `$${n.toFixed(2)}` });
    });
    return Array.from(map.values()).sort((a, b) => Number(a.value) - Number(b.value));
  }, [records]);

  // True when a search/filter is active — cards should mirror what's on-screen.
  const viewActive = Boolean(hasActiveFilters(view) || (view.search && view.search.trim()));

  const filteredTotals = useMemo(() => {
    const acc = {
      records: displayRecords.length,
      cash_total: 0, cash_count: 0,
      eft_total: 0,  eft_count: 0,
      total: 0,
    };
    displayRecords.forEach((r) => {
      const amt = Number(r.amount) || 0;
      acc.total += amt;
      if ((r.type || "cash").toLowerCase() === "eft") { acc.eft_total += amt; acc.eft_count += 1; }
      else { acc.cash_total += amt; acc.cash_count += 1; }
    });
    ["cash_total", "eft_total", "total"].forEach((k) => {
      acc[k] = Math.round(acc[k] * 100) / 100;
    });
    return acc;
  }, [displayRecords]);

  const activeSummary = viewActive ? {
    ...summary,
    records: filteredTotals.records,
    cash_total: filteredTotals.cash_total, cash_count: filteredTotals.cash_count,
    eft_total: filteredTotals.eft_total,   eft_count: filteredTotals.eft_count,
    total: filteredTotals.total,
  } : summary;

  // ---- fetch ----
  const load = useCallback(async () => {
    if (team === "adherence") return;  // AdherencePanel owns its data (avoids 400s from the ledger)
    const cacheKey = `${team}|${month}|${day}`;
    const cached = DATA_CACHE.get(cacheKey);
    if (cached) {
      // Show last-loaded data instantly; refresh silently below.
      setRecords(cached.records); setSummary(cached.summary); setReasons(cached.reasons);
      setTopups(cached.topups); setCashVerifs(cached.cashVerifs);
    } else {
      setLoading(true);
    }
    try {
      const params = { team, month };
      if (day && day !== "all") params.day = day;
      const [recRes, sumRes, reasonsRes, topupsRes, verifsRes] = await Promise.all([
        api.get("/incentives/records", { params }),
        api.get("/incentives/summary", { params: { team, month, ...(day && day !== "all" ? { day } : {}) } }),
        api.get("/incentives/reasons"),
        api.get("/incentives/topups", { params: { team, month } }),
        api.get("/incentives/cash-verifications", { params: { team } }),
      ]);
      const next = {
        records: recRes.data?.items || [],
        summary: sumRes.data || {},
        reasons: reasonsRes.data?.items || [],
        topups: topupsRes.data?.items || [],
        cashVerifs: verifsRes.data?.items || [],
      };
      DATA_CACHE.set(cacheKey, next);
      setRecords(next.records);
      setSummary(next.summary);
      setReasons(next.reasons);
      setTopups(next.topups);
      setCashVerifs(next.cashVerifs);
    } catch (e) {
      // 401 handler in api.js will redirect; other errors stay silent to avoid spam
    } finally {
      setLoading(false);
    }
  }, [team, month, day]);

  useEffect(() => { load(); }, [load, refreshKey]);

  // Ended months are read-only for staff (admins can still modify).
  const monthLocked = !!summary.ended && !isAdmin;

  // When a record is requested (from global search on Incentives tab),
  // switch team/month to match the record and pop open its edit dialog.
  useEffect(() => {
    if (!openRequest || !openRequest.record) return;
    const rec = openRequest.record;
    if (rec.team && /^team_\d+$/.test(rec.team)) {
      setTeam(rec.team);
    }
    if (rec.date && /^\d{4}-\d{2}-\d{2}$/.test(rec.date)) {
      setMonth(rec.date.slice(0, 7));
    }
    setDay("all");
    setEditing({ ...rec });
  }, [openRequest]);

  // Changing team keeps the selected day so staff can compare the same date.
  // Changing month (or leaving/remounting the Rewards tab) resets to all days.
  useEffect(() => { setDay("all"); }, [month]);

  const activeTeamLabel = teams.find((t) => t.key === team)?.label || (lockTeam && team === lockTeam ? (lockTeamLabel || "") : "");
  const activeMonthLabel = months.find((m) => m.key === month)?.label || month;
  const dayOptions = useMemo(() => {
    const days = summary.days_with_records || [];
    return day !== "all" && !days.includes(day) ? [day, ...days] : days;
  }, [summary.days_with_records, day]);

  // ---- CRUD handlers ----
  const newRecordDate = () =>
    `${month}-${String(new Date().getDate()).padStart(2, "0")}`
      .replace(/^(\d{4}-\d{2})-(\d{2})$/, (_, mo, d) => {
        // clamp to last day of month
        const [y, m] = mo.split("-").map(Number);
        const lastDay = new Date(y, m, 0).getDate();
        const dayNum = Math.min(parseInt(d, 10), lastDay);
        return `${mo}-${String(dayNum).padStart(2, "0")}`;
      });

  const openNew = () => {
    const dflt = newRecordDate();
    // Cash-float gate FIRST: when the record's date has no verification for
    // this team, the validation screen comes up before the form ever opens.
    if (!verifiedCashDates.has(dflt)) {
      setAddAfterVerify(true);
      setPendingCashDate(dflt);
      return;
    }
    setEditing({
      __new: true,
      team,
      date: dflt,
      first_name: "",
      last_name: "",
      reason: "",
      type: "cash",
      amount: "",
    });
  };

  const openEdit = (r) => setEditing({ ...r });

  const closeEdit = () => setEditing(null);

  const saveRecord = async () => {
    if (!editing) return;
    const body = {
      team,
      date: editing.date,
      first_name: (editing.first_name || "").trim(),
      last_name: (editing.last_name || "").trim(),
      reason: (editing.reason || "").trim(),
      type: (editing.type || "cash").toLowerCase(),
      amount: parseFloat(editing.amount) || 0,
    };
    if (!body.date) { toast.error("Date is required"); return; }
    if (!body.amount || body.amount <= 0) { toast.error("Amount must be greater than 0"); return; }
    // Cash-float gate: the record's date must have a cash verification for
    // this team — intercept and send them to the Remaining quadrant first.
    if (editing.__new && !verifiedCashDates.has(body.date)) {
      setPendingCashDate(body.date);
      return;
    }
    try {
      if (editing.__new) {
        await api.post("/incentives/records", body);
        toast.success("Record added");
      } else {
        // date/team belong to path record - team not updated
        await api.patch(`/incentives/records/${editing.id}`, {
          date: body.date,
          first_name: body.first_name,
          last_name: body.last_name,
          reason: body.reason,
          type: body.type,
          amount: body.amount,
        });
        toast.success("Record updated");
      }
      closeEdit();
      await load();
    } catch (e) {
      // Server-side backstop for the same gate (e.g. verified state changed)
      if (e?.response?.status === 409) {
        setPendingCashDate(body.date);
        return;
      }
      toast.error(e?.response?.data?.detail || "Failed to save record");
    }
  };

  const deleteRecord = async () => {
    if (!editing || editing.__new) { closeEdit(); return; }
    if (!window.confirm("Delete this incentive record?")) return;
    try {
      await api.delete(`/incentives/records/${editing.id}`);
      toast.success("Record deleted");
      closeEdit();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete record");
    }
  };

  const pastePatient = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (!text || !text.trim()) { toast.error("Clipboard is empty"); return; }
      const parsed = parsePatientLabel(text);
      let filled = 0;
      const patch = {};
      if (parsed.first_name) { patch.first_name = parsed.first_name; filled += 1; }
      if (parsed.last_name)  { patch.last_name  = parsed.last_name;  filled += 1; }
      if (filled === 0) {
        toast.error("Couldn't find a name in the clipboard");
        return;
      }
      setEditing((e) => ({ ...(e || {}), ...patch }));
      toast.success(`Autofilled ${filled} field${filled === 1 ? "" : "s"}`);
    } catch {
      toast.error("Couldn't read clipboard — grant permission or paste manually");
    }
  };

  // ============================ render =====================================
  // Adherence tab is a client-driven flow with its own endpoints — swap the
  // entire panel body when it's selected (share the same team-toggle strip).
  if (team === "adherence") {
    return (
      <AdherencePanel
        team={team}
        setTeam={setTeam}
        teams={teams.filter((t) => t.key !== "adherence")}
        month={month}
        setMonth={setMonth}
        day={day}
        setDay={setDay}
        months={months}
        monthLabel={activeMonthLabel}
        refreshKey={refreshKey}
      />
    );
  }

  return (
    <div data-testid="incentives-panel" style={{
      background: "#fff",
      borderRadius: 14,
      border: `1px solid ${BORDER}`,
      padding: 20,
    }}>
      {/* Local styles for the segmented Team + Day + Month pill and the
          horizontally-scrolling records table on mobile. */}
      <style>{`
        .inc-team-month {
          display: flex; align-items: stretch; border: 1px solid #e2e8f0;
          background: #fff; border-radius: 6px; overflow: hidden; width: 100%;
        }
        .inc-team-btn {
          flex: 1; height: 36px; padding: 0 12px; font-size: 12px; font-weight: 600;
          color: #64748b; background: #fff; border: 0;
          border-right: 1px solid #e2e8f0; cursor: pointer;
        }
        .inc-team-btn.active { background: ${NAVY}; color: #fff; }
        .inc-team-btn.hq { /* desktop: still a normal 1/4 pill */ }
        .inc-day-select, .inc-month-select {
          flex: 1; height: 36px; padding: 0 30px 0 12px; font-size: 12px; font-weight: 600;
          color: #fff; background: ${NAVY}; border: 0; text-align: center;
          -webkit-appearance: none; appearance: none;
          background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='white'><path fill-rule='evenodd' d='M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.06l3.71-3.83a.75.75 0 1 1 1.08 1.04l-4.25 4.38a.75.75 0 0 1-1.08 0L5.21 8.27a.75.75 0 0 1 .02-1.06z' clip-rule='evenodd'/></svg>");
          background-repeat: no-repeat; background-position: right 8px center;
          cursor: pointer;
        }
        .inc-day-select { border-right: 1px solid rgba(255,255,255,0.20); }
        .inc-balance-pill { padding: 8px 9px !important; font-size: 12.5px !important; }
        .inc-add-btn { padding: 8px 12px !important; font-size: 12.5px !important; white-space: nowrap; }
        /* The balance pill must NEVER resize — same padding/font at every
           viewport, so it looks identical on Rewards and Expenses always. */
        @media (max-width: 640px) {
          .inc-cash-header { flex-wrap: wrap; row-gap: 8px; }
          .inc-cash-verified { order: 3; flex: 1 0 100%; justify-content: center; box-sizing: border-box; }
          .inc-cash-actions { order: 2; margin-left: auto; }
          .inc-team-month { flex-wrap: wrap; }
          /* Row 1 = teams (thirds), HQ on its own full-width bar,
             Row 3 = All days | Month (two halves) — same rhythm as Expenses. */
          .inc-team-btn { flex: 1 1 33%; border-bottom: 1px solid #e2e8f0; }
          .inc-team-btn:nth-of-type(3) { border-right: 0; }
          .inc-team-btn.hq { flex: 1 1 100%; border-right: 0; }
          .inc-team-btn:last-of-type { border-right: 0; }
          .inc-day-select, .inc-month-select { flex: 1 1 50%; }
          /* staff view: single locked team — everything fits on one line */
          .inc-team-month.locked { flex-wrap: nowrap; }
          .inc-team-month.locked .inc-team-btn { flex: 0 0 auto; border-bottom: 0; }
          .inc-team-month.locked .inc-day-select,
          .inc-team-month.locked .inc-month-select { flex: 1 1 0; min-width: 0; padding: 0 24px 0 8px; }
        }
        .inc-tbl-scroll { overflow-x: auto; margin: 0 -6px; }
        .inc-tbl { width: 100%; min-width: 810px; border-collapse: collapse; font-size: 13px; }
        .inc-tbl th { text-align: left; font-size: 10px; font-weight: 700;
          color: ${MUTED}; text-transform: uppercase; letter-spacing: 0.14em;
          padding: 8px 10px; border-bottom: 1px solid ${BORDER}; white-space: nowrap; }
        .inc-tbl td { padding: 10px; border-bottom: 1px solid ${BORDER}; color: ${TEXT};
          vertical-align: middle; white-space: nowrap; }
        .inc-tbl td.reason { white-space: normal; min-width: 180px; }
        .inc-tbl tr:hover td { background: #FAFAF7; }
        .inc-icon-btn { background: none; border: none; cursor: pointer;
          color: ${MUTED}; padding: 4px; margin-left: 2px; }
        .inc-icon-btn:hover { color: ${DARK_GREEN}; }
        .inc-icon-btn.danger:hover { color: #B4351B; }
        .inc-pill { display: inline-block; padding: 2px 8px; border-radius: 999px;
          font-size: 11px; font-weight: 600; }
        .inc-pill-cash { background: rgba(46,125,91,0.10); color: ${TAG_CASH}; }
        .inc-pill-eft { background: rgba(74,111,176,0.10); color: ${TAG_EFT}; }
        .inc-verify-btn { font-family: inherit; font-size: 11.5px; font-weight: 700; cursor: pointer;
          border: 1.5px solid ${TAG_CASH}; color: ${TAG_CASH}; background: #fff;
          border-radius: 999px; padding: 4px 14px;
          display: inline-flex; align-items: center; gap: 5px;
          transition: background .15s, color .15s; }
        .inc-verify-btn:hover { background: ${TAG_CASH}; color: #fff; }
        .inc-verified-state { display: inline-flex; flex-direction: column; gap: 1px;
          cursor: pointer; background: none; border: 0; padding: 0;
          font-family: inherit; text-align: left; }
        .inc-verified-pill { display: inline-flex; align-items: center; gap: 5px;
          background: rgba(46,125,91,0.12); color: ${TAG_CASH};
          border-radius: 999px; padding: 4px 12px; font-size: 11.5px; font-weight: 700; }
        .inc-verified-when { font-size: 10px; color: ${MUTED}; padding-left: 4px; }
        .inc-verified-state:hover .inc-verified-pill { background: rgba(180,53,27,0.10); color: #B4351B; }
        @keyframes inc-pulse { 0%,100% { box-shadow: 0 0 0 0 rgba(46,125,91,0.35); } 50% { box-shadow: 0 0 0 6px rgba(46,125,91,0); } }
      `}</style>

      {/* Title + verification badge + actions; badge wraps below on mobile */}
      <div className="inc-cash-header" style={{
        display: "flex", alignItems: "center", justifyContent: "flex-start",
        gap: 12, marginBottom: 14,
      }}>
        <h2 style={{
          fontFamily: "'Work Sans', system-ui, sans-serif",
          fontSize: 18, fontWeight: 700, margin: 0,
        }}>
          Cash
        </h2>
        {verifiedCashDates.has(todayStr) && (
          <span
            data-testid="cash-verified-today"
            className="inc-cash-verified"
            style={{
              display: "inline-flex", alignItems: "center", gap: 5,
              fontSize: 11, fontWeight: 700, color: TAG_CASH,
              background: "rgba(46,125,91,0.10)", borderRadius: 999,
              padding: "6px 10px",
            }}
          >
            <Check style={{ width: 11, height: 11 }} strokeWidth={3} />
            Cash verified today
          </span>
        )}
        <div className="inc-cash-actions" style={{ display: "flex", gap: 6, flexShrink: 0, minWidth: 0, flexWrap: "nowrap", marginLeft: "auto" }}>
          <button
            type="button"
            data-testid="incentives-balance-btn"
            className="inc-balance-pill"
            onClick={() => setBalanceOpen(true)}
            title="Cash balance for this team + month"
            style={{
              background: "#fff",
              color: (summary.remaining || 0) < 0 ? "#B4351B" : DARK_GREEN,
              border: `1px solid ${(summary.remaining || 0) < 0 ? "#E9BFB1" : "rgba(15,59,46,0.25)"}`,
              borderRadius: 8, padding: "8px 9px",
              fontSize: 12.5, fontWeight: 700,
              minWidth: 62, justifyContent: "center",
              display: "inline-flex", alignItems: "center", gap: 5,
              cursor: "pointer", whiteSpace: "nowrap",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            <span aria-hidden className="inc-balance-emoji" style={{ fontSize: 13 }}>💰</span>
            {money(summary.remaining || 0)}
          </button>
          <Button
            data-testid="incentives-add-btn"
            className="inc-add-btn"
            onClick={openNew}
            disabled={monthLocked}
            title={monthLocked ? "Month ended — only an admin can make changes" : undefined}
            style={{
              background: DARK_GREEN, color: "#fff", border: 0,
              borderRadius: 8, padding: "8px 12px", fontSize: 12.5, fontWeight: 600,
              display: "inline-flex", alignItems: "center", gap: 6,
              cursor: monthLocked ? "not-allowed" : "pointer",
              opacity: monthLocked ? 0.55 : 1,
            }}
          >
            <Plus style={{ width: 15, height: 15 }} strokeWidth={2.5} />
            Add record
          </Button>
        </div>
      </div>

      {/* Team + Day + Month strip */}
      <div className={`inc-team-month${lockTeam ? " locked" : ""}`} data-testid="incentives-team-month" style={{ marginBottom: 14 }}>
        {(lockTeam
          ? (teams.some((t) => t.key === lockTeam)
              ? teams.filter((t) => t.key === lockTeam)
              // Registry fetch hasn't landed yet (or is cached without this
              // team) — render the locked pill immediately so the strip
              // never gains a segment late and shifts the page.
              : [{ key: lockTeam, label: lockTeamLabel || lockTeam }])
          : teams
        ).map((t) => (
          <button
            key={t.key}
            type="button"
            data-testid={`incentives-team-${t.key}`}
            onClick={() => setTeam(t.key)}
            className={`inc-team-btn${t.key === team ? " active" : ""}${t.key === "adherence" ? " hq" : ""}`}
          >
            {t.label}
          </button>
        ))}
        <select
          data-testid="incentives-day-select"
          className="inc-day-select"
          value={day}
          onChange={(e) => setDay(e.target.value)}
        >
          <option value="all" style={{ color: "#000" }}>All days</option>
          {dayOptions.map((d) => (
            <option key={d} value={d} style={{ color: "#000" }}>
              {fromYMD(d) ? format(fromYMD(d), "MMM d") : d}
            </option>
          ))}
        </select>
        <select
          data-testid="incentives-month-select"
          className="inc-month-select"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
        >
          {months.map((m) => (
            <option key={m.key} value={m.key} style={{ color: "#000" }}>{m.label}</option>
          ))}
        </select>
      </div>

      {/* Totals strip */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
        gap: 10, marginBottom: 14,
      }}>
        <TotalCard testId="incentives-total-records"
          label="Records" value={activeSummary.records || 0}
          sub={viewActive ? "Filtered view" : (day === "all" ? "This month" : "Selected day")}
        />
        <TotalCard testId="incentives-total-cash"
          label="Cash" value={money(activeSummary.cash_total || 0)}
          sub={`${activeSummary.cash_count || 0} record${(activeSummary.cash_count || 0) === 1 ? "" : "s"}`}
        />
        <TotalCard testId="incentives-total-eft"
          label="EFT" value={money(activeSummary.eft_total || 0)}
          sub={`${activeSummary.eft_count || 0} record${(activeSummary.eft_count || 0) === 1 ? "" : "s"}`}
        />
        <TotalCard testId="incentives-total-paid"
          label="Total paid" value={money(activeSummary.total || 0)}
          sub={viewActive ? "Filtered view" : `${activeTeamLabel.split(" ")[0]} ${activeTeamLabel.split(" ")[1] || ""} · ${activeMonthLabel}`}
          strong
        />
      </div>

      {/* Records table — keep the current rows on screen while a reload
          (e.g. after Verify) runs in the background, so confirming an action
          never collapses the page and jumps the user to the top. */}
      {loading && records.length === 0 ? (
        <div style={{ color: MUTED, padding: 30, textAlign: "center" }}>Loading…</div>
      ) : records.length === 0 ? (
        <div data-testid="incentives-empty" style={{
          border: `1px dashed ${BORDER}`, borderRadius: 10, padding: 32,
          textAlign: "center", color: MUTED,
        }}>
          No incentive records for {activeTeamLabel} in {activeMonthLabel}
          {day !== "all" ? ` on ${format(fromYMD(day) || new Date(), "MMM d")}` : ""}.
          <div style={{ marginTop: 8, fontSize: 12 }}>
            Tap <b>+ Add record</b> to log a payout.
          </div>
        </div>
      ) : (
        <div className="inc-tbl-scroll">
          <div style={{
            display: "flex", alignItems: "center", gap: 8, padding: "0 6px 8px",
            flexWrap: "nowrap",
          }}>
            <input
              data-testid="incentives-search"
              type="text"
              value={view.search || ""}
              onChange={(e) => updateView({ search: e.target.value })}
              placeholder="Search name, description…"
              style={{
                flex: 1, minWidth: 0, height: 34,
                padding: "0 10px", fontSize: 13,
                border: `1px solid ${BORDER}`, borderRadius: 8,
                background: "#fff", color: TEXT, boxSizing: "border-box",
              }}
            />
            {(view.sortKey || hasActiveFilters(view) || view.search) && (
              <button
                type="button"
                data-testid="incentives-view-clear"
                onClick={() => updateView({ sortKey: null, sortDir: null, filters: {}, search: "" })}
                style={{
                  background: "rgba(180,53,27,0.08)", border: `1px solid #E9BFB1`,
                  borderRadius: 8, padding: "6px 12px",
                  fontSize: 12, fontWeight: 700, color: "#B4351B", cursor: "pointer",
                  display: "inline-flex", alignItems: "center", gap: 6,
                }}
              >
                <XIcon style={{ width: 12, height: 12 }} strokeWidth={2.5} />
                Clear all filters
              </button>
            )}
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, marginLeft: "auto" }}>
              <div data-testid="incentives-shown-count" style={{ fontSize: 12, color: MUTED, whiteSpace: "nowrap" }}>
                {displayRecords.length} of {records.length}
              </div>
              <button
                type="button"
                data-testid="incentives-copy-selection"
                title="Copy the currently visible rows (date · full name · amount) to your clipboard"
                onClick={async () => {
                  if (displayRecords.length === 0) {
                    toast.error("Nothing to copy — no visible rows.");
                    return;
                  }
                  let running = 0;
                  const lines = displayRecords.map((r) => {
                    const dateStr = fromYMD(r.date) ? format(fromYMD(r.date), "MMM d, yyyy") : (r.date || "");
                    const name = `${(r.first_name || "").trim()} ${(r.last_name || "").trim()}`.trim() || "—";
                    const amt = Number(r.amount) || 0;
                    running += amt;
                    return `${dateStr}\t${name}\t${money(amt)}`;
                  });
                  lines.push("");
                  lines.push(`Total\t${displayRecords.length} record${displayRecords.length === 1 ? "" : "s"}\t${money(Math.round(running * 100) / 100)}`);
                  const text = lines.join("\n");
                  try {
                    await navigator.clipboard.writeText(text);
                    toast.success(`Copied ${displayRecords.length} row${displayRecords.length === 1 ? "" : "s"} + total to clipboard.`);
                  } catch {
                    toast.error("Couldn't access clipboard — long-press the toast to copy manually.");
                  }
                }}
                style={{
                  background: "#fff", border: `1px solid ${BORDER}`,
                  borderRadius: 8, padding: "4px 10px",
                  fontSize: 12, fontWeight: 700, color: DARK_GREEN, cursor: "pointer",
                  display: "inline-flex", alignItems: "center", gap: 5, whiteSpace: "nowrap",
                }}
              >
                <Copy style={{ width: 12, height: 12 }} strokeWidth={2.5} />
                Copy
              </button>
            </div>
          </div>
          <table className="inc-tbl" data-testid="incentives-table">
            <thead>
              <tr>
                <TableColumnHeader columnKey="date" label="Date" type="date"
                  values={uniqDates.map((d) => d)} view={view} onChange={updateView}
                  testId="incentives" />
                <TableColumnHeader columnKey="first_name" label="First name"
                  values={uniqFirstNames} view={view} onChange={updateView}
                  testId="incentives" />
                <TableColumnHeader columnKey="last_name" label="Last name"
                  values={uniqLastNames} view={view} onChange={updateView}
                  testId="incentives" />
                <TableColumnHeader columnKey="reason" label="Description"
                  values={uniqReasons} view={view} onChange={updateView}
                  testId="incentives" />
                <TableColumnHeader columnKey="type" label="Type"
                  values={uniqTypes} view={view} onChange={updateView}
                  testId="incentives" />
                <TableColumnHeader columnKey="amount" label="Amount" type="number" align="right"
                  values={uniqAmounts} view={view} onChange={updateView}
                  testId="incentives" />
                <TableColumnHeader columnKey="verified" label="Verified"
                  values={["Not verified", "Verified"]} view={view} onChange={updateView}
                  testId="incentives" />
                <th style={{ textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayRecords.length === 0 && (
                <tr data-testid="incentives-no-matches">
                  <td colSpan={8} style={{ padding: 24, textAlign: "center", color: MUTED }}>
                    No records match your search or filter.
                  </td>
                </tr>
              )}
              {displayRecords.map((r) => (
                <tr key={r.id} data-testid={`incentives-row-${r.id}`}>
                  <td>{fromYMD(r.date) ? format(fromYMD(r.date), "MMM d, yyyy") : r.date}</td>
                  <td>{r.first_name}</td>
                  <td>{r.last_name}</td>
                  <td className="reason">{r.reason || "—"}</td>
                  <td>
                    <span className={`inc-pill inc-pill-${r.type === "eft" ? "eft" : "cash"}`}>
                      {r.type === "eft" ? "EFT" : "Cash"}
                    </span>
                  </td>
                  <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>
                    {money(r.amount)}
                  </td>
                  <td>
                    {r.verified ? (
                      isAdmin ? (
                        <button
                          type="button"
                          className="inc-verified-state"
                          onClick={() => toggleVerify(r)}
                          title="Verified — click to undo"
                          data-testid={`incentives-unverify-${r.id}`}
                        >
                          <span className="inc-verified-pill">
                            <Check style={{ width: 11, height: 11 }} strokeWidth={3} />
                            Verified
                          </span>
                          <span className="inc-verified-when">
                            {fmtDate(String(r.verified_at || "").slice(0, 10))}
                            {r.verified_by ? ` · ${initials(r.verified_by)}` : ""}
                          </span>
                        </button>
                      ) : (
                        <span className="inc-verified-state" data-testid={`incentives-verified-static-${r.id}`}>
                          <span className="inc-verified-pill">
                            <Check style={{ width: 11, height: 11 }} strokeWidth={3} />
                            Verified
                          </span>
                          <span className="inc-verified-when">
                            {fmtDate(String(r.verified_at || "").slice(0, 10))}
                            {r.verified_by ? ` · ${initials(r.verified_by)}` : ""}
                          </span>
                        </span>
                      )
                    ) : (
                      isAdmin ? (
                        <button
                          type="button"
                          className="inc-verify-btn"
                          onClick={() => toggleVerify(r)}
                          title="Confirm this record matches the system"
                          data-testid={`incentives-verify-${r.id}`}
                        >
                          Verify
                        </button>
                      ) : (
                        <span style={{ fontSize: 11.5, color: MUTED }} data-testid={`incentives-unverified-static-${r.id}`}>Not verified</span>
                      )
                    )}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    {(!isAdmin && r.verified) || monthLocked ? (
                      <span
                        title={monthLocked ? "Month ended — only an admin can make changes" : "Verified — locked (only an admin can edit)"}
                        data-testid={`incentives-edit-locked-${r.id}`}
                        style={{ color: MUTED, display: "inline-flex", padding: 4 }}
                      >
                        <Lock style={{ width: 14, height: 14 }} strokeWidth={2} />
                      </span>
                    ) : (
                      <button
                        type="button"
                        className="inc-icon-btn"
                        data-testid={`incentives-edit-${r.id}`}
                        onClick={() => openEdit(r)}
                        title="Edit"
                      >
                        <Pencil style={{ width: 15, height: 15 }} strokeWidth={2} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add / Edit record dialog */}
      <RecordDialog
        editing={editing}
        setEditing={setEditing}
        onClose={closeEdit}
        onSave={saveRecord}
        onDelete={deleteRecord}
        onPastePatient={pastePatient}
        reasons={reasons}
        onOpenManageReasons={() => setManageOpen(true)}
        activeTeamLabel={activeTeamLabel}
        activeMonthLabel={activeMonthLabel}
        isAdmin={isAdmin}
        verifiedCashDates={verifiedCashDates}
      />

      {/* Cash-verification intercept — comes up BEFORE the Add form when the
          record's date has no cash verification for this team */}
      {pendingCashDate && !balanceOpen && !monthLocked && (
        <div
          data-testid="cash-verify-intercept"
          style={{
            position: "fixed", inset: 0, zIndex: 100,
            background: "rgba(15,59,46,0.55)",
            display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
          }}
        >
          <div style={{
            background: "#fff", borderRadius: 16, padding: 22,
            width: "100%", maxWidth: 440, boxShadow: "0 20px 60px rgba(15,59,46,0.25)",
          }}>
            <div style={{
              width: 44, height: 44, borderRadius: "50%", marginBottom: 12,
              background: "rgba(180,53,27,0.10)", color: "#B4351B",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 20, fontWeight: 800,
            }}>!</div>
            <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 6 }}>Cash not verified for this date</div>
            <div style={{ fontSize: 13, lineHeight: 1.55, marginBottom: 16 }}>
              {activeTeamLabel}&apos;s cash float was never verified for <b style={{ color: "#B4351B" }}>{format(fromYMD(pendingCashDate) || new Date(), "EEEE, MMM d, yyyy")}</b>.
              Before any record can be entered on that date, open the <b>Cash balance</b> and
              click the <b>Remaining</b> amount to verify it — takes ten seconds.
            </div>
            <button
              type="button"
              data-testid="cash-verify-go"
              onClick={() => setBalanceOpen(true)}
              style={{
                background: DARK_GREEN, color: "#fff", border: 0, borderRadius: 8,
                padding: "10px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer",
                width: "100%", fontFamily: "inherit",
              }}
            >
              Verify cash for this date
            </button>
            <button
              type="button"
              data-testid="cash-verify-back"
              onClick={() => { setPendingCashDate(null); setAddAfterVerify(false); }}
              style={{
                background: "#fff", color: MUTED, border: `1px solid ${BORDER}`, borderRadius: 8,
                padding: "10px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer",
                width: "100%", marginTop: 8, fontFamily: "inherit",
              }}
            >
              Go back
            </button>
          </div>
        </div>
      )}

      {/* Manage reasons dialog */}
      <ManageReasonsDialog
        open={manageOpen}
        onOpenChange={setManageOpen}
        reasons={reasons}
        onChanged={() => load()}
      />

      {/* Cash balance dialog */}
      <BalanceDialog
        open={balanceOpen}
        onOpenChange={(o) => { setBalanceOpen(o); if (!o) setPendingCashDate(null); }}
        team={team}
        month={month}
        activeTeamLabel={activeTeamLabel}
        activeMonthLabel={activeMonthLabel}
        topups={topups}
        summary={summary}
        cashVerifs={cashVerifs}
        pendingCashDate={pendingCashDate}
        isAdmin={isAdmin}
        onCashVerified={() => {
          // Verify → then open the Add form automatically when the flow
          // started from the Add-record button (no form was ever shown).
          // Close the balance dialog first so the two never stack.
          const reopen = addAfterVerify && !editing;
          setPendingCashDate(null);
          setAddAfterVerify(false);
          if (reopen) {
            setBalanceOpen(false);
            setEditing({
              __new: true, team, date: newRecordDate(),
              first_name: "", last_name: "", reason: "", type: "cash", amount: "",
            });
          }
          load();
        }}
        onChanged={() => load()}
      />
    </div>
  );
}

// ============================================================================
function TotalCard({ label, value, sub, strong = false, testId }) {
  return (
    <div data-testid={testId} style={{
      border: `1px solid ${BORDER}`, borderRadius: 10, padding: "10px 12px",
      background: "#F9F8F6",
    }}>
      <div style={{
        fontSize: 10, fontWeight: 700, letterSpacing: "0.18em",
        textTransform: "uppercase", color: LABEL_GREEN,
      }}>{label}</div>
      <div style={{
        fontSize: 20, fontWeight: 700, marginTop: 4,
        color: strong ? DARK_GREEN : TEXT,
      }}>{value}</div>
      <div style={{ fontSize: 11, color: MUTED, marginTop: 2 }}>{sub}</div>
    </div>
  );
}

// ============================================================================
function RecordDialog({
  editing, setEditing, onClose, onSave, onDelete,
  onPastePatient, reasons, onOpenManageReasons,
  activeTeamLabel, activeMonthLabel, isAdmin = false,
  verifiedCashDates = new Set(),
}) {
  if (!editing) return null;
  const isNew = !!editing.__new;

  return (
    <Dialog open={!!editing} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="incentives-record-dialog" className="max-w-md max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            gap: 8, paddingTop: 10, paddingRight: 28,
          }}>
            <DialogTitle style={{ flex: 1, minWidth: 0 }}>Add record</DialogTitle>
            <button
              type="button"
              data-testid="incentives-paste-patient"
              onClick={onPastePatient}
              title="Auto-fill first + last name from clipboard"
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                height: 30, padding: "0 10px",
                background: "rgba(15,59,46,0.06)", color: DARK_GREEN,
                border: "1px solid rgba(15,59,46,0.25)",
                borderRadius: 8, cursor: "pointer",
                fontSize: 12, fontWeight: 600, fontFamily: "inherit",
                flexShrink: 0, whiteSpace: "nowrap",
              }}
            >
              <ClipboardPaste style={{ width: 13, height: 13 }} strokeWidth={2} />
              Paste patient
            </button>
          </div>
          <DialogDescription>
            {activeTeamLabel} · {activeMonthLabel}
          </DialogDescription>
        </DialogHeader>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <p style={labelStyle}>Date</p>
            <DatePickerButton
              testId="incentives-field-date"
              value={editing.date || ""}
              onChange={(v) => setEditing({ ...editing, date: v })}
            />
            {editing.__new && editing.date && (
              verifiedCashDates.has(editing.date) ? (
                <div data-testid="cash-date-ok" style={{ fontSize: 11, color: TAG_CASH, fontWeight: 600, marginTop: 4 }}>
                  ✓ Cash verified for this date
                </div>
              ) : (
                <div data-testid="cash-date-warn" style={{ fontSize: 11, color: "#B4351B", fontWeight: 600, marginTop: 4 }}>
                  ⚠ Cash not verified for this date — you&apos;ll be asked to verify before saving
                </div>
              )
            )}
          </div>
          <div style={{ minWidth: 0 }}>
            <p style={labelStyle}>Type</p>
            <select
              data-testid="incentives-field-type"
              value={editing.type || "cash"}
              onChange={(e) => setEditing({ ...editing, type: e.target.value })}
              style={{
                ...inputStyle,
                appearance: "none",
                WebkitAppearance: "none",
                paddingRight: 30,
                backgroundImage:
                  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='%236B7A73'><path fill-rule='evenodd' d='M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.06l3.71-3.83a.75.75 0 1 1 1.08 1.04l-4.25 4.38a.75.75 0 0 1-1.08 0L5.21 8.27a.75.75 0 0 1 .02-1.06z' clip-rule='evenodd'/></svg>\")",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 8px center",
              }}
            >
              <option value="cash">Cash</option>
              <option value="eft">EFT</option>
            </select>
          </div>
          <div style={{ minWidth: 0 }}>
            <p style={labelStyle}>First name</p>
            <input
              data-testid="incentives-field-first-name"
              value={editing.first_name || ""}
              onChange={(e) => setEditing({ ...editing, first_name: e.target.value })}
              style={inputStyle}
              placeholder="Joel"
            />
          </div>
          <div style={{ minWidth: 0 }}>
            <p style={labelStyle}>Last name</p>
            <input
              data-testid="incentives-field-last-name"
              value={editing.last_name || ""}
              onChange={(e) => setEditing({ ...editing, last_name: e.target.value })}
              style={inputStyle}
              placeholder="Leduc"
            />
          </div>
          <div style={{ gridColumn: "1 / -1", minWidth: 0 }}>
            <p style={labelStyle}>Reason</p>
            <select
              data-testid="incentives-field-reason"
              value={editing.reason || ""}
              onChange={(e) => setEditing({ ...editing, reason: e.target.value })}
              style={{
                ...inputStyle,
                appearance: "none",
                WebkitAppearance: "none",
                paddingRight: 30,
                backgroundImage:
                  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='%236B7A73'><path fill-rule='evenodd' d='M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.06l3.71-3.83a.75.75 0 1 1 1.08 1.04l-4.25 4.38a.75.75 0 0 1-1.08 0L5.21 8.27a.75.75 0 0 1 .02-1.06z' clip-rule='evenodd'/></svg>\")",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 8px center",
              }}
            >
              <option value="">Select reason…</option>
              {reasons.map((r) => (
                <option key={r.id} value={r.name}>{r.name}</option>
              ))}
            </select>
            <div style={{ marginTop: 6, textAlign: "right" }}>
              <button
                type="button"
                data-testid="incentives-manage-reasons-link"
                onClick={onOpenManageReasons}
                style={{
                  background: "none", border: 0, padding: 0,
                  color: DARK_GREEN, fontSize: 11, fontWeight: 600,
                  cursor: "pointer", textDecoration: "underline",
                }}
              >
                Manage reasons
              </button>
            </div>
          </div>
          <div style={{ gridColumn: "1 / -1", minWidth: 0 }}>
            <p style={labelStyle}>Amount</p>
            <div style={{ position: "relative" }}>
              <span style={{
                position: "absolute", left: 12, top: "50%",
                transform: "translateY(-50%)", color: MUTED,
                fontWeight: 600, fontSize: 14,
              }}>$</span>
              <input
                data-testid="incentives-field-amount"
                type="text"
                inputMode="decimal"
                value={editing.amount ?? ""}
                onChange={(e) => setEditing({ ...editing, amount: e.target.value.replace(/[^0-9.]/g, "") })}
                style={{ ...inputStyle, paddingLeft: 26 }}
                placeholder="40.00"
              />
            </div>
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, marginTop: 18 }}>
          <button
            type="button"
            data-testid="incentives-save-btn"
            onClick={onSave}
            style={{
              flex: 1, background: DARK_GREEN, color: "#fff", border: 0,
              borderRadius: 8, padding: "10px 12px", fontSize: 13, fontWeight: 600,
              cursor: "pointer",
            }}
          >
            {isNew ? "Save record" : "Update record"}
          </button>
          {!isNew && (isAdmin || !editing.verified) && (
            <button
              type="button"
              data-testid="incentives-delete-btn"
              onClick={onDelete}
              style={{
                background: "#fff", color: "#B4351B",
                border: "1px solid #E9BFB1", borderRadius: 8,
                padding: "10px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer",
              }}
            >
              Delete
            </button>
          )}
          {!isNew && !isAdmin && editing.verified && (
            <span data-testid="incentives-delete-locked" style={{ fontSize: 11.5, color: MUTED, alignSelf: "center" }}>
              Verified — only an admin can delete
            </span>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
function ManageReasonsDialog({ open, onOpenChange, reasons, onChanged }) {
  const [rows, setRows] = useState([]);
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setRows(reasons.map((r) => ({ ...r, _draft: r.name })));
  }, [open, reasons]);

  const saveRow = async (r) => {
    if (!r._draft.trim() || r._draft.trim() === r.name) return;
    setBusy(true);
    try {
      await api.patch(`/incentives/reasons/${r.id}`, { name: r._draft.trim() });
      toast.success("Reason renamed");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rename failed");
    } finally {
      setBusy(false);
    }
  };

  const deleteRow = async (r) => {
    if (!window.confirm(`Delete reason "${r.name}"?`)) return;
    setBusy(true);
    try {
      await api.delete(`/incentives/reasons/${r.id}`);
      toast.success("Reason deleted");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  const addRow = async () => {
    const name = newName.trim();
    if (!name) return;
    setBusy(true);
    try {
      await api.post("/incentives/reasons", { name });
      setNewName("");
      toast.success("Reason added");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Add failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="incentives-manage-reasons-dialog" className="max-w-md max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Manage reasons</DialogTitle>
          <DialogDescription>
            Add, rename or delete reasons in the dropdown. Shared across both teams.
          </DialogDescription>
        </DialogHeader>

        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 14 }}>
          {rows.length === 0 && (
            <div style={{ color: MUTED, fontSize: 13, padding: 12, textAlign: "center" }}>
              No reasons yet — add one below.
            </div>
          )}
          {rows.map((r, idx) => (
            <div
              key={r.id}
              data-testid={`incentives-reason-row-${r.id}`}
              style={{
                display: "flex", gap: 8, alignItems: "center",
                padding: "8px 10px", border: `1px solid ${BORDER}`,
                borderRadius: 8,
              }}
            >
              <input
                value={r._draft}
                onChange={(e) => setRows((rs) => rs.map((x, i) => i === idx ? { ...x, _draft: e.target.value } : x))}
                onBlur={() => saveRow(r)}
                data-testid={`incentives-reason-input-${r.id}`}
                style={{ flex: 1, border: 0, padding: 0, background: "transparent", fontSize: 14, color: TEXT, outline: "none" }}
              />
              {r._draft.trim() && r._draft.trim() !== r.name && (
                <button
                  type="button"
                  className="inc-icon-btn"
                  onClick={() => saveRow(r)}
                  data-testid={`incentives-reason-save-${r.id}`}
                  title="Save"
                >
                  <Check style={{ width: 15, height: 15, color: TAG_CASH }} strokeWidth={2.5} />
                </button>
              )}
              <button
                type="button"
                className="inc-icon-btn danger"
                onClick={() => deleteRow(r)}
                data-testid={`incentives-reason-delete-${r.id}`}
                title="Delete"
              >
                <Trash2 style={{ width: 15, height: 15 }} strokeWidth={2} />
              </button>
            </div>
          ))}
        </div>

        <div style={{ display: "flex", gap: 8 }}>
          <input
            data-testid="incentives-new-reason-input"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") addRow(); }}
            placeholder="Add new reason…"
            style={{ ...inputStyle, flex: 1 }}
          />
          <button
            type="button"
            data-testid="incentives-new-reason-add"
            onClick={addRow}
            disabled={!newName.trim() || busy}
            style={{
              background: DARK_GREEN, color: "#fff", border: 0,
              borderRadius: 8, padding: "0 16px", fontSize: 13, fontWeight: 600,
              cursor: !newName.trim() || busy ? "default" : "pointer",
              opacity: !newName.trim() || busy ? 0.6 : 1,
            }}
          >
            Add
          </button>
        </div>

        <div style={{ marginTop: 18 }}>
          <button
            type="button"
            data-testid="incentives-manage-reasons-done"
            onClick={() => onOpenChange(false)}
            style={{
              width: "100%", background: DARK_GREEN, color: "#fff", border: 0,
              borderRadius: 8, padding: "10px 12px", fontSize: 13, fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Done
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
function BalanceDialog({
  open, onOpenChange, team, month, activeTeamLabel, activeMonthLabel,
  topups, summary, onChanged, cashVerifs = [], pendingCashDate = null, onCashVerified,
  isAdmin = false,
}) {
  const todayYMD = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };
  // The date is always the real day the cash was added — the top-up still
  // counts toward the selected ledger month even when it's a past month.
  const defaultDate = () => todayYMD();

  const [date, setDate] = useState(defaultDate());
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmingCash, setConfirmingCash] = useState(false);
  const [confirmingEndMonth, setConfirmingEndMonth] = useState(false);

  // Ended months: staff see a read-only ledger; only admins can modify.
  const ended = !!summary.ended;
  const locked = ended && !isAdmin;

  // Label of the ledger month right after the one being viewed — used by
  // the End Month transfer copy.
  const nextMonthLabel = (() => {
    const [y, m] = (month || "").split("-").map(Number);
    if (!y || !m) return "next month";
    return format(new Date(y, m, 1), "MMMM yyyy"); // month index m = first of NEXT month
  })();
  const nextMonthShort = (() => {
    const [y, m] = (month || "").split("-").map(Number);
    if (!y || !m) return "next month";
    return format(new Date(y, m, 1), "MMM yyyy");
  })();

  // Which date the Remaining quadrant verifies: the pending record's date
  // (when arriving from the intercept) or today.
  const targetCashDate = pendingCashDate || defaultDate();
  const targetVerif = cashVerifs.find((v) => v.date === targetCashDate) || null;

  useEffect(() => {
    if (open) {
      setDate(defaultDate());
      setAmount("");
      setConfirmingCash(false);
      setConfirmingEndMonth(false);
    }
  }, [open, month, pendingCashDate]);

  const verifyCash = async () => {
    setBusy(true);
    try {
      await api.post("/incentives/cash-verifications", {
        team, date: targetCashDate, amount: Number(summary.remaining || 0),
      });
      toast.success(`Cash verified for ${format(fromYMD(targetCashDate) || new Date(), "MMM d, yyyy")}`);
      setConfirmingCash(false);
      if (onCashVerified) onCashVerified();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't verify cash");
    } finally {
      setBusy(false);
    }
  };
  const addTopup = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error("Amount must be greater than 0"); return; }
    if (!date) { toast.error("Date is required"); return; }
    setBusy(true);
    try {
      await api.post("/incentives/topups", { team, date, amount: amt, month });
      toast.success("Top-up added");
      setAmount("");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add top-up");
    } finally {
      setBusy(false);
    }
  };

  const deleteTopup = async (t) => {
    if (!window.confirm(`Delete top-up of ${money(t.amount)} on ${format(fromYMD(t.date) || new Date(), "MMM d, yyyy")}?`)) return;
    setBusy(true);
    try {
      await api.delete(`/incentives/topups/${t.id}`);
      toast.success("Top-up deleted");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete top-up");
    } finally {
      setBusy(false);
    }
  };

  // End Month: zero out this month's remaining cash and carry it into next
  // month as a "Transferred in" top-up (verified on the first working day
  // through the existing cash-verification gate).
  const endMonth = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/incentives/transfer-month", { team, month });
      toast.success(`Transferred ${money(data?.amount ?? summary.remaining ?? 0)} to ${nextMonthLabel}`);
      setConfirmingEndMonth(false);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to end month");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="incentives-balance-dialog" className="max-w-md max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Cash balance</DialogTitle>
          <DialogDescription>
            <b>{activeTeamLabel}</b> · {activeMonthLabel}
            <div style={{ fontSize: 11, marginTop: 4 }}>
              Each team keeps its own cash budget.
            </div>
          </DialogDescription>
        </DialogHeader>

        {/* Summary strip */}
        <div style={{
          display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8,
          marginBottom: 16,
        }}>
          <MiniTile testId="incentives-balance-cash-in"
            label="Cash in" value={money(summary.topups_total || 0)}
            sub={`${summary.topups_count || 0} top-up${(summary.topups_count || 0) === 1 ? "" : "s"}`}
          />
          <MiniTile testId="incentives-balance-paid-out"
            label="Paid out" value={money(summary.month_paid_total || 0)}
            sub={`${summary.month_paid_records || 0} record${(summary.month_paid_records || 0) === 1 ? "" : "s"}`}
          />
          <div
            role="button"
            tabIndex={0}
            data-testid="incentives-balance-remaining"
            onClick={() => !targetVerif && !locked && setConfirmingCash(true)}
            onKeyDown={(e) => { if (e.key === "Enter" && !targetVerif && !locked) setConfirmingCash(true); }}
            title={targetVerif ? "Cash verified for this date" : locked ? "Month ended — only an admin can make changes" : "Click to verify the cash float for this date"}
            style={{
              border: targetVerif ? `1.5px solid ${TAG_CASH}` : `1.5px dashed ${TAG_CASH}`,
              borderRadius: 10, padding: "8px 10px",
              background: targetVerif ? "rgba(46,125,91,0.05)" : "#F9F8F6",
              cursor: targetVerif || locked ? "default" : "pointer",
              animation: targetVerif || locked ? "none" : "inc-pulse 1.8s infinite",
            }}
          >
            <div style={{
              fontSize: 9, fontWeight: 700, letterSpacing: "0.16em",
              textTransform: "uppercase", color: LABEL_GREEN,
            }}>Remaining</div>
            <div style={{
              fontSize: 16, fontWeight: 800, marginTop: 2, fontVariantNumeric: "tabular-nums",
              color: (summary.remaining || 0) < 0 ? "#B4351B" : DARK_GREEN,
            }}>{money(summary.remaining || 0)}</div>
            {targetVerif ? (
              <div data-testid="cash-verified-stamp" style={{ fontSize: 10, color: TAG_CASH, fontWeight: 700, marginTop: 3 }}>
                ✓ Verified {format(new Date(targetVerif.created_at), "h:mm a")}
                {targetVerif.verified_by ? ` · ${targetVerif.verified_by}` : ""}
              </div>
            ) : locked ? null : (
              <div style={{ fontSize: 10, color: TAG_CASH, fontWeight: 700, marginTop: 3 }}>
                ◈ Click to verify cash for {format(fromYMD(targetCashDate) || new Date(), "MMM d")}
              </div>
            )}
          </div>
        </div>

        {/* Carry-over note — Remaining is a running float, so when earlier
            months left cash over we say so explicitly. */}
        {(summary.carried_over || 0) !== 0 && (
          <div data-testid="incentives-balance-carryover" style={{
            fontSize: 11, color: MUTED, marginTop: -8, marginBottom: 16,
          }}>
            Remaining includes <b>{money(summary.carried_over)}</b> carried over from earlier months.
          </div>
        )}

        {/* Daily cash verification confirm */}
        {confirmingCash && !targetVerif && (
          <div data-testid="cash-verify-confirm" style={{
            border: `1.5px solid ${TAG_CASH}`, borderRadius: 12, padding: 14,
            background: "rgba(46,125,91,0.05)", marginBottom: 14,
          }}>
            <div style={{ fontSize: 13, lineHeight: 1.55, marginBottom: 12 }}>
              <b>Daily cash verification</b><br />
              I confirm {activeTeamLabel}&apos;s cash float for <b>{format(fromYMD(targetCashDate) || new Date(), "EEEE, MMM d, yyyy")}</b> is <b>{money(summary.remaining || 0)}</b>, matching the Remaining balance.
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                type="button"
                data-testid="cash-verify-confirm-btn"
                onClick={verifyCash}
                disabled={busy}
                style={{
                  flex: 1, background: TAG_CASH, color: "#fff", border: 0, borderRadius: 8,
                  padding: "10px 14px", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                }}
              >
                ✓ Verify cash on hand
              </button>
              <button
                type="button"
                onClick={() => setConfirmingCash(false)}
                style={{
                  background: "#fff", color: MUTED, border: `1px solid ${BORDER}`, borderRadius: 8,
                  padding: "10px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Add top-up form */}
        {/* Add top-up — hidden for staff once the month is ended */}
        {!locked && (
        <div style={{ borderTop: `1px solid ${BORDER}`, paddingTop: 14, marginBottom: 14 }}>
          <div style={{ ...labelStyle, marginBottom: 8 }}>Add top-up</div>
          <div style={{ fontSize: 11, color: MUTED, marginTop: -4, marginBottom: 8 }}>
            Counts toward {activeMonthLabel}.
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div style={{ minWidth: 0 }}>
              <p style={{ ...labelStyle, fontSize: 10 }}>Date</p>
              <DatePickerButton
                testId="incentives-topup-date"
                value={date}
                onChange={(v) => setDate(v)}
              />
            </div>
            <div style={{ minWidth: 0 }}>
              <p style={{ ...labelStyle, fontSize: 10 }}>Amount</p>
              <div style={{ position: "relative" }}>
                <span style={{
                  position: "absolute", left: 12, top: "50%",
                  transform: "translateY(-50%)", color: MUTED,
                  fontWeight: 600, fontSize: 14,
                }}>$</span>
                <input
                  data-testid="incentives-topup-amount"
                  type="text"
                  inputMode="decimal"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ""))}
                  placeholder="200"
                  style={{ ...inputStyle, paddingLeft: 26 }}
                />
              </div>
            </div>
          </div>
          <button
            type="button"
            data-testid="incentives-topup-add"
            onClick={addTopup}
            disabled={busy || !amount}
            style={{
              width: "100%", marginTop: 10,
              background: DARK_GREEN, color: "#fff", border: 0,
              borderRadius: 8, padding: "10px 12px", fontSize: 13, fontWeight: 600,
              cursor: busy || !amount ? "default" : "pointer",
              opacity: busy || !amount ? 0.6 : 1,
              display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
            }}
          >
            <Plus style={{ width: 15, height: 15 }} strokeWidth={2.5} />
            Add top-up
          </button>
        </div>
        )}

        {/* Top-ups list */}
        <div style={{ ...labelStyle, marginBottom: 8 }}>This month&apos;s top-ups</div>
        {topups.length === 0 ? (
          <div style={{
            border: `1px dashed ${BORDER}`, borderRadius: 8, padding: 16,
            textAlign: "center", color: MUTED, fontSize: 13,
          }}>
            No top-ups yet for {activeMonthLabel}.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {topups.map((t) => (
              <div
                key={t.id}
                data-testid={`incentives-topup-row-${t.id}`}
                style={{
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  padding: "8px 10px", border: `1px solid ${BORDER}`, borderRadius: 8,
                }}
              >
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>
                    {fromYMD(t.date) ? format(fromYMD(t.date), "MMM d, yyyy") : t.date}
                  </div>
                  <div style={{ fontSize: 11, color: MUTED }}>{t.note || "Added top-up"}</div>
                </div>
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <div style={{
                    fontWeight: 700, color: (Number(t.amount) || 0) < 0 ? "#B4351B" : DARK_GREEN,
                    fontVariantNumeric: "tabular-nums",
                  }}>{money(t.amount)}</div>
                  {!locked && (
                  <button
                    type="button"
                    className="inc-icon-btn danger"
                    onClick={() => deleteTopup(t)}
                    data-testid={`incentives-topup-delete-${t.id}`}
                    title="Delete top-up"
                  >
                    <Trash2 style={{ width: 15, height: 15 }} strokeWidth={2} />
                  </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Daily cash verifications — the audit trail behind the Add-record gate */}
        <div style={{ borderTop: `1px solid ${BORDER}`, paddingTop: 12, marginTop: 16 }}>
          <div style={{ ...labelStyle, marginBottom: 8 }}>Daily cash verifications</div>
          {cashVerifs.length === 0 ? (
            <div
              data-testid="cash-verifs-empty"
              style={{
                border: `1px dashed ${BORDER}`, borderRadius: 8, padding: 16,
                textAlign: "center", color: MUTED, fontSize: 13,
              }}
            >
              No cash verifications yet for {activeTeamLabel.split(" · ")[0]}.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {cashVerifs.map((v) => (
                <div
                  key={v.id}
                  data-testid={`cash-verif-${v.date}`}
                  style={{
                    display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8,
                    padding: "8px 10px", border: `1px solid ${BORDER}`, borderRadius: 8,
                  }}
                >
                  <div style={{ fontSize: 13, fontWeight: 600 }}>
                    {fromYMD(v.date) ? format(fromYMD(v.date), "MMM d, yyyy") : v.date}
                  </div>
                  <div style={{ fontSize: 11, color: MUTED }}>
                    {v.verified_by || "Staff"} · {format(new Date(v.created_at), "h:mm a")}
                  </div>
                  <div style={{ fontWeight: 700, color: TAG_CASH, fontVariantNumeric: "tabular-nums" }}>
                    {money(v.amount)}
                  </div>
                </div>
              ))}
            </div>
          )}
          <div style={{ fontSize: 11, color: MUTED, marginTop: 6 }}>
            A record can only be saved on a date that appears here.
          </div>
        </div>

        {/* End-of-month transfer confirm */}
        {confirmingEndMonth && (
          <div data-testid="end-month-confirm" style={{
            border: "1.5px solid #B4351B", borderRadius: 12, padding: 14,
            background: "rgba(180,53,27,0.05)", marginTop: 16,
          }}>
            <div style={{ fontSize: 13, lineHeight: 1.55, marginBottom: 12 }}>
              <b>End {activeMonthLabel} for {activeTeamLabel}?</b><br />
              Remaining <b>{money(summary.remaining || 0)}</b> transfers to {nextMonthShort}.
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "nowrap" }}>
              <button
                type="button"
                data-testid="end-month-confirm-btn"
                onClick={endMonth}
                disabled={busy}
                style={{
                  flex: 1, background: "#B4351B", color: "#fff", border: 0, borderRadius: 8,
                  padding: "10px 14px", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                }}
              >
                Transfer {money(summary.remaining || 0)}
              </button>
              <button
                type="button"
                data-testid="end-month-cancel"
                onClick={() => setConfirmingEndMonth(false)}
                style={{
                  background: "#fff", color: MUTED, border: `1px solid ${BORDER}`, borderRadius: 8,
                  padding: "10px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {ended && (
          <div data-testid="incentives-month-ended-note" style={{
            marginTop: 16, fontSize: 12, color: MUTED, textAlign: "center", lineHeight: 1.5,
          }}>
            This month has been ended — the remaining cash was transferred to {nextMonthShort}.
            {locked ? " Only an admin can make changes." : ""}
          </div>
        )}

        <div style={{ marginTop: 18, display: "flex", gap: 8, flexWrap: "nowrap" }}>
          <button
            type="button"
            data-testid="incentives-balance-done"
            onClick={() => onOpenChange(false)}
            style={{
              flex: 1, background: DARK_GREEN, color: "#fff", border: 0,
              borderRadius: 8, padding: "10px 12px", fontSize: 13, fontWeight: 600,
              cursor: "pointer", whiteSpace: "nowrap",
            }}
          >
            Done
          </button>
          {!ended && (
          <button
            type="button"
            data-testid="incentives-end-month"
            onClick={() => setConfirmingEndMonth(true)}
            disabled={busy || (summary.remaining || 0) <= 0}
            title={(summary.remaining || 0) <= 0 ? "No remaining cash to transfer" : `Transfer the remaining ${money(summary.remaining || 0)} to ${nextMonthLabel}`}
            style={{
              flex: 1, background: "#fff",
              color: (summary.remaining || 0) <= 0 ? MUTED : "#B4351B",
              border: `1px solid ${(summary.remaining || 0) <= 0 ? BORDER : "rgba(180,53,27,0.45)"}`,
              borderRadius: 8, padding: "10px 12px", fontSize: 13, fontWeight: 700,
              cursor: (summary.remaining || 0) <= 0 ? "not-allowed" : "pointer",
              whiteSpace: "nowrap", opacity: (summary.remaining || 0) <= 0 ? 0.6 : 1,
            }}
          >
            End Month
          </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
function MiniTile({ label, value, sub, strong = false, danger = false, testId }) {
  return (
    <div data-testid={testId} style={{
      border: `1px solid ${BORDER}`, borderRadius: 10, padding: "8px 10px",
      background: "#F9F8F6",
    }}>
      <div style={{
        fontSize: 9, fontWeight: 700, letterSpacing: "0.16em",
        textTransform: "uppercase", color: LABEL_GREEN,
      }}>{label}</div>
      <div style={{
        fontSize: 16, fontWeight: 700, marginTop: 2,
        color: danger ? "#B4351B" : (strong ? DARK_GREEN : TEXT),
        fontVariantNumeric: "tabular-nums",
      }}>{value}</div>
      <div style={{ fontSize: 10, color: MUTED, marginTop: 2 }}>{sub}</div>
    </div>
  );
}
